/**
 * Signing Modal
 * Handles the doctor signature flow with Vidaas ICP-Brasil
 * Uses popup + polling approach to keep user in the app
 */

import { useState, useEffect, useRef } from 'react';
import { consents } from '../../api/client';

const STEPS = {
  CONFIRM: 'confirm',
  CREATING: 'creating',
  READY: 'ready',
  SIGNING: 'signing',
  SUCCESS: 'success',
  ERROR: 'error',
};

const POLL_INTERVAL = 2500; // Poll every 2.5 seconds

export default function SigningModal({
  isOpen,
  onClose,
  consentData, // Form data from the current consent being created
  clinicId,
  onSuccess,
  existingConsentId, // Optional: skip create step for existing consents
  existingConsentStatus, // Optional: 'DRAFT' or 'PENDING_DOCTOR_SIGNATURE'
}) {
  const isExisting = !!existingConsentId;
  const skipFinalize = existingConsentStatus === 'PENDING_DOCTOR_SIGNATURE';

  const [step, setStep] = useState(
    isExisting && skipFinalize ? STEPS.READY : STEPS.CONFIRM
  );
  const [consentId, setConsentId] = useState(existingConsentId || null);
  const [patientSigningUrl, setPatientSigningUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Popup and polling refs
  const popupRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Listen for postMessage from Vidaas callback popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'vidaas-signing-complete') {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Poll for consent status changes
  const startPolling = (id) => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const consent = await consents.get(id);
        
        // Check if signing completed
        if (consent.status === 'PENDING_PATIENT_SIGNATURE' || consent.status === 'COMPLETED') {
          // Stop polling
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          // Close popup if still open
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
          
          // Build patient signing URL
          const baseUrl = window.location.origin;
          setPatientSigningUrl(`${baseUrl}/sign/${consent.patientSigningToken}`);
          
          // Move to success
          setStep(STEPS.SUCCESS);
          
          // Callback
          if (onSuccess) {
            onSuccess(consent);
          }
        }
        
        // Check if popup was closed without completing
        if (popupRef.current && popupRef.current.closed && step === STEPS.SIGNING) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          // Go back to ready state so they can try again
          setError('A janela foi fechada antes de concluir a assinatura. Se o app Vidaas não abriu, verifique se ele está instalado no seu celular.');
          setStep(STEPS.READY);
        }
      } catch (err) {
        console.error('Polling error:', err);
        // Don't stop polling on error, could be transient
      }
    }, POLL_INTERVAL);
  };

  if (!isOpen) return null;

  // Step 1: Create consent on API and finalize (or just finalize for existing)
  const handleCreateAndFinalize = async () => {
    setStep(STEPS.CREATING);
    setError(null);

    try {
      let id = consentId;

      if (!isExisting) {
        // Create new consent
        const consent = await consents.create({
          clinicId,
          patientId: consentData.patientId || undefined,
          patientName: consentData.patientName,
          patientCpf: consentData.patientCpf,
          patientEmail: consentData.patientEmail || undefined,
          patientPhone: consentData.patientPhone || undefined,
          procedures: consentData.procedures,
          consultationDate: consentData.consultationDate,
        });
        id = consent.id;
        setConsentId(id);
      }

      // Finalize
      await consents.finalize(id);

      setStep(STEPS.READY);
    } catch (err) {
      console.error('Failed to create/finalize consent:', err);
      setError(err.message || 'Erro ao processar documento');
      setStep(STEPS.ERROR);
    }
  };

  // Step 2: Start Vidaas signing flow in popup
  const handleStartSigning = async () => {
    setError(null);

    // Open popup IMMEDIATELY in the synchronous click handler.
    // Mobile browsers (especially Safari) block window.open() after any await.
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      'about:blank',
      'vidaas-signing',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      setError('Popup bloqueado. Por favor, permita popups para este site.');
      return;
    }

    // Show loading message while we fetch the authorization URL
    popup.document.write(
      '<html><head><title>Vidaas</title></head>' +
      '<body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;background:#f9fafb;">' +
      '<div style="text-align:center"><p style="font-size:18px;color:#374151;">Carregando assinatura...</p>' +
      '<p style="font-size:14px;color:#6b7280;margin-top:8px;">Aguarde um momento.</p></div></body></html>'
    );

    popupRef.current = popup;

    try {
      const result = await consents.startDoctorSigning(consentId);

      // Navigate the already-open popup to the Vidaas URL
      popup.location.href = result.authorizationUrl;

      // Start polling for status changes
      setStep(STEPS.SIGNING);
      startPolling(consentId);

    } catch (err) {
      console.error('Failed to start signing:', err);
      popup.close();
      popupRef.current = null;
      setError(err.message || 'Erro ao iniciar assinatura');
      setStep(STEPS.ERROR);
    }
  };

  // Cancel signing in progress
  const handleCancelSigning = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    setStep(STEPS.READY);
  };

  // Reset modal state
  const handleClose = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    setStep(isExisting && skipFinalize ? STEPS.READY : STEPS.CONFIRM);
    if (!isExisting) setConsentId(null);
    setPatientSigningUrl(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  // Copy link to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      prompt('Copie o link:', text);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={step !== STEPS.SIGNING ? handleClose : undefined}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
          {/* Close button (hidden during signing) */}
          {step !== STEPS.SIGNING && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* STEP: Confirm */}
          {step === STEPS.CONFIRM && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isExisting ? 'Finalizar e Assinar' : 'Enviar para Assinatura Digital'}
              </h3>

              <p className="text-gray-600 mb-6">
                {isExisting
                  ? 'O documento será finalizado e você poderá assiná-lo com seu certificado digital ICP-Brasil (Vidaas).'
                  : 'O documento será criado e você poderá assiná-lo com seu certificado digital ICP-Brasil (Vidaas).'}
              </p>

              {consentData && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">Resumo:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li><strong>Paciente:</strong> {consentData.patientName}</li>
                    <li><strong>CPF:</strong> {consentData.patientCpf}</li>
                    <li><strong>Procedimentos:</strong> {consentData.procedures?.join(', ')}</li>
                    <li><strong>Data:</strong> {consentData.consultationDate}</li>
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateAndFinalize}
                  className="flex-1 py-3 px-4 bg-indigo-600 rounded-lg text-white font-medium hover:bg-indigo-700 transition-colors"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* STEP: Creating */}
          {step === STEPS.CREATING && (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 mb-4">
                <svg className="animate-spin w-full h-full text-indigo-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Criando documento...
              </h3>
              <p className="text-gray-600">
                Aguarde enquanto preparamos o documento para assinatura.
              </p>
            </div>
          )}

          {/* STEP: Ready to Sign */}
          {step === STEPS.READY && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Documento Pronto
              </h3>
              
              <p className="text-gray-600 mb-6">
                O documento foi criado. Clique abaixo para assinar com seu certificado digital ICP-Brasil.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start mb-2">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-amber-800 font-medium">
                    O app Vidaas precisa estar instalado no seu celular.
                  </p>
                </div>
                <p className="text-sm text-amber-700 ml-7 mb-3">
                  No computador, será exibido um QR code para escanear. No celular, o app Vidaas será aberto automaticamente.
                </p>
                <div className="flex gap-2 ml-7">
                  <a
                    href="https://apps.apple.com/br/app/vidaas/id1458498828"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 px-2.5 py-1.5 rounded-md transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    App Store
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=br.com.validcertificadora.vidaas"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 px-2.5 py-1.5 rounded-md transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M3.18 23.67L14.85 12 3.18.33c-.12.14-.18.33-.18.55v22.24c0 .22.06.41.18.55zm1.4.82L16.85 13.4l-3.15-3.15L4.58 24.49zM20.16 10.88L17.5 9.37 14.1 12l3.4 3.4 2.66-1.5c.73-.42.73-1.44 0-2.02zM4.58-.49L15.7 13.75l3.15-3.15L4.58-.49z"/></svg>
                    Google Play
                  </a>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-left">
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                  {error.includes('Vidaas') && (
                    <p className="text-sm text-red-600 mt-1">
                      Verifique se o app Vidaas está instalado e se seu certificado está ativo.
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={handleStartSigning}
                className="w-full py-3 px-4 bg-indigo-600 rounded-lg text-white font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Assinar com ICP-Brasil
              </button>
            </div>
          )}

          {/* STEP: Signing in progress (popup open, polling) */}
          {step === STEPS.SIGNING && (
            <div className="text-center py-4">
              <div className="mx-auto w-20 h-20 mb-6 relative">
                {/* Pulsing circle animation */}
                <div className="absolute inset-0 bg-indigo-100 rounded-full animate-ping opacity-75" />
                <div className="relative w-full h-full bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Aguardando Assinatura
              </h3>
              
              <p className="text-gray-600 mb-2">
                Complete a assinatura no app Vidaas do seu celular.
              </p>
              
              <p className="text-sm text-gray-500 mb-6">
                Escaneie o QR code na janela aberta e aprove a assinatura.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Verificando status da assinatura...</span>
                </div>
              </div>

              <button
                onClick={handleCancelSigning}
                className="w-full py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}

          {/* STEP: Success */}
          {step === STEPS.SUCCESS && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Documento Assinado!
              </h3>
              
              <p className="text-gray-600 mb-6">
                Sua assinatura digital foi registrada. Agora envie o link abaixo para o paciente assinar.
              </p>

              {patientSigningUrl && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Link para assinatura do paciente:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={patientSigningUrl}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(patientSigningUrl)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        copied 
                          ? 'bg-green-100 text-green-700 border border-green-300' 
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {copied ? '✓' : 'Copiar'}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const text = `Olá! Por favor, assine o termo de consentimento acessando o link: ${patientSigningUrl}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
                  }}
                  className="flex-1 py-3 px-4 bg-green-600 rounded-lg text-white font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          )}

          {/* STEP: Error */}
          {step === STEPS.ERROR && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Erro
              </h3>
              
              <p className="text-gray-600 mb-2">
                Ocorreu um erro ao processar o documento.
              </p>
              
              {error && (
                <p className="text-red-600 text-sm mb-6 bg-red-50 p-3 rounded-lg">
                  {error}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
                <button
                  onClick={() => setStep(STEPS.CONFIRM)}
                  className="flex-1 py-3 px-4 bg-indigo-600 rounded-lg text-white font-medium hover:bg-indigo-700 transition-colors"
                >
                  Tentar Novamente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
